import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDate as formatDateUtil, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  ClipboardList,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  User,
  Loader2,
  ChevronRight,
  Link as LinkIcon,
  MessageSquare,
  Flag,
  Edit,
  Trash2,
  Paperclip,
  Upload,
  Download,
  FileText,
  RefreshCw,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that can see this widget (only those who can assign tasks to others)
const TASK_ASSIGN_ROLES = ['system_admin', 'executive', 'operations_manager'];

const TasksAssignedWidget = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  const [summary, setSummary] = useState({ open: 0, overdue: 0, completed: 0 });
  
  // Edit dialog state
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingEditAttachments, setPendingEditAttachments] = useState([]);  // For edit task attachments
  const [isDraggingEdit, setIsDraggingEdit] = useState(false);  // For drag-and-drop
  const [uploadProgress, setUploadProgress] = useState({});  // Track upload progress
  
  // Reopen task comment dialog (for admins reopening completed tasks)
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [reopeningComment, setReopeningComment] = useState('');
  const [pendingReopenStatus, setPendingReopenStatus] = useState(null);
  const [originalTaskStatus, setOriginalTaskStatus] = useState(null);
  
  // Delete confirmation state
  const [deletingTask, setDeletingTask] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Staff list for reassignment
  const [staffList, setStaffList] = useState([]);
  
  // Attachment states
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showAttachmentsDialog, setShowAttachmentsDialog] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);

  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Dynamic dropdown values
  const [taskPriorities, setTaskPriorities] = useState([
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ]);
  const [taskStatuses, setTaskStatuses] = useState([
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ]);

  // Only render for users with assign permissions
  const canAssignToOthers = TASK_ASSIGN_ROLES.includes(user?.role);

  const fetchAssignedTasks = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setRefreshing(true);
      
      // For sysadmin, executive, and operations_manager: show ALL tasks assigned to others
      // This gives visibility into all tasks in the organization
      let url = `${API}/tasks?assigned_to_others=true&limit=50`;
      
      if (activeTab === 'completed') {
        url += '&status=completed';
      } else if (activeTab === 'overdue') {
        url += '&status=overdue';
      } else {
        url += '&status=open';
      }
      
      const response = await axios.get(url);
      setTasks(response.data.items || []);
      
      // Fetch summary counts for tasks assigned to others
      fetchSummary();
    } catch (error) {
      console.error('Failed to fetch assigned tasks:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (canAssignToOthers) {
      fetchAssignedTasks();
      fetchStaffList();
      fetchDropdowns();
    }
  }, [activeTab, canAssignToOthers, fetchAssignedTasks]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh || !canAssignToOthers) return;
    const interval = setInterval(() => fetchAssignedTasks(false), 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, canAssignToOthers, fetchAssignedTasks]);
  
  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setStaffList(response.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [prioritiesRes, statusesRes] = await Promise.all([
        axios.get(`${API}/dropdowns/task_priorities`),
        axios.get(`${API}/dropdowns/task_statuses`)
      ]);
      if (prioritiesRes.data.items?.length > 0) {
        setTaskPriorities(prioritiesRes.data.items);
      }
      if (statusesRes.data.items?.length > 0) {
        setTaskStatuses(statusesRes.data.items);
      }
    } catch (error) {
      console.error('Failed to fetch dropdowns:', error);
    }
  };

  const fetchSummary = async () => {
    try {
      // Fetch all tasks assigned to others (for oversight view)
      const [openRes, overdueRes, completedRes] = await Promise.all([
        axios.get(`${API}/tasks?assigned_to_others=true&status=open&limit=200`),
        axios.get(`${API}/tasks?assigned_to_others=true&status=overdue&limit=200`),
        axios.get(`${API}/tasks?assigned_to_others=true&status=completed&limit=200`)
      ]);
      
      const openCount = openRes.data.total || 0;
      const overdueCount = overdueRes.data.total || 0;
      const completedCount = completedRes.data.total || 0;
      
      setSummary({ open: openCount, overdue: overdueCount, completed: completedCount });
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'low': return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const handleEditTask = async (task) => {
    // Fetch fresh task data to get all details including attachments
    try {
      const response = await axios.get(`${API}/tasks/${task.id}`);
      setEditingTask({
        ...response.data,
        due_date: response.data.due_date ? response.data.due_date.split('T')[0] : ''
      });
      setOriginalTaskStatus(response.data.status);  // Track original status for reopen detection
    } catch (error) {
      // Fall back to the task data we have
      setEditingTask({
        ...task,
        due_date: task.due_date ? task.due_date.split('T')[0] : ''
      });
      setOriginalTaskStatus(task.status);  // Track original status for reopen detection
    }
    setPendingEditAttachments([]);
    setShowEditDialog(true);
  };
  
  // Handle multiple files for edit task
  const handleAddEditAttachments = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    
    for (const file of fileArray) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
      } else {
        validFiles.push(file);
      }
    }
    
    if (validFiles.length > 0) {
      setPendingEditAttachments(prev => [...prev, ...validFiles]);
    }
  };

  // Functions for edit task attachments
  const handleAddEditAttachment = (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    setPendingEditAttachments(prev => [...prev, file]);
  };

  const removeEditPendingAttachment = (index) => {
    setPendingEditAttachments(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[`edit_${index}`];
      return newProgress;
    });
  };

  // Drag and drop handlers for edit task
  const handleDragOverEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingEdit(true);
  };

  const handleDragLeaveEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingEdit(false);
  };

  const handleDropEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingEdit(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleAddEditAttachments(files);
    }
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    if (!editingTask || !window.confirm('Delete this attachment?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${editingTask.id}/attachments/${attachmentId}`);
      // Update the editing task's attachments list
      setEditingTask(prev => ({
        ...prev,
        attachments: prev.attachments.filter(a => a.id !== attachmentId)
      }));
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };
  
  const handleSaveTask = async (reopenComment = null) => {
    if (!editingTask) return;
    
    // Check if this is a reopen scenario (completed -> open/in_progress by admin)
    // All users of this widget are admins (TASK_ASSIGN_ROLES)
    const isReopening = originalTaskStatus === 'completed' && 
                       ['open', 'in_progress'].includes(editingTask.status);
    
    // If reopening and no comment provided, show the reopen dialog
    if (isReopening && !reopenComment && !showReopenDialog) {
      setPendingReopenStatus(editingTask.status);
      setShowReopenDialog(true);
      return;
    }
    
    setSaving(true);
    try {
      // Build update payload
      const updatePayload = {
        title: editingTask.title,
        description: editingTask.description,
        priority: editingTask.priority,
        due_date: editingTask.due_date || null,
        status: editingTask.status,
        assigned_to: editingTask.assigned_to
      };
      
      // Add reopening comment if reopening
      if (isReopening && reopenComment) {
        updatePayload.reopening_comment = reopenComment;
      }
      
      await axios.put(`${API}/tasks/${editingTask.id}`, updatePayload);
      
      // Upload any pending attachments with progress tracking
      if (pendingEditAttachments.length > 0) {
        for (let i = 0; i < pendingEditAttachments.length; i++) {
          const file = pendingEditAttachments[i];
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${API}/tasks/${editingTask.id}/attachments`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(prev => ({ ...prev, [`edit_${i}`]: percentCompleted }));
              }
            });
          } catch (err) {
            console.error('Failed to upload attachment:', err);
            toast.error(`Failed to upload: ${file.name}`);
          }
        }
      }
      
      toast.success(isReopening ? 'Task reopened successfully' : 'Task updated successfully');
      setShowEditDialog(false);
      setShowReopenDialog(false);
      setEditingTask(null);
      setOriginalTaskStatus(null);
      setPendingEditAttachments([]);
      setUploadProgress({});
      setReopeningComment('');
      setPendingReopenStatus(null);
      fetchAssignedTasks();
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle the reopen comment submission
  const handleConfirmReopen = () => {
    if (!reopeningComment.trim()) {
      toast.error('Please enter a reason for reopening this task');
      return;
    }
    handleSaveTask(reopeningComment);
  };
  
  // Cancel the reopen dialog and revert status
  const handleCancelReopen = () => {
    setShowReopenDialog(false);
    setReopeningComment('');
    setPendingReopenStatus(null);
    // Revert the status back to completed
    if (editingTask) {
      setEditingTask(prev => ({ ...prev, status: 'completed' }));
    }
  };
  
  const handleDeleteTask = (task) => {
    setDeletingTask(task);
    setShowDeleteDialog(true);
  };
  
  const confirmDeleteTask = async () => {
    if (!deletingTask) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/tasks/${deletingTask.id}`);
      setShowDeleteDialog(false);
      setDeletingTask(null);
      fetchAssignedTasks();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    } finally {
      setDeleting(false);
    }
  };

  const handleFileUpload = async (taskId, file) => {
    if (!file) return;
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/tasks/${taskId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('File uploaded successfully');
      fetchAssignedTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (taskId, attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchAssignedTasks();
      if (viewingTask && viewingTask.id === taskId) {
        const updatedTask = tasks.find(t => t.id === taskId);
        setViewingTask(updatedTask);
      }
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const openAttachmentsDialog = (task) => {
    setViewingTask(task);
    setShowAttachmentsDialog(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Download attachment with authentication
  const handleDownloadAttachment = async (attachment) => {
    try {
      const url = attachment.file_url.startsWith('http') 
        ? attachment.file_url 
        : `${BACKEND_URL}${attachment.file_url}`;
      
      // Fetch with auth headers
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.file_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  const openViewDialog = async (task) => {
    try {
      const response = await axios.get(`${API}/tasks/${task.id}`);
      setViewingTask(response.data);
      setShowViewDialog(true);
    } catch (error) {
      setViewingTask(task);
      setShowViewDialog(true);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return formatDateUtil(dateStr) || '';
  };

  // Don't render if user doesn't have assign permissions or no tasks assigned to others
  if (!canAssignToOthers) {
    return null;
  }

  // Don't render if there are no tasks assigned to others
  if (!loading && summary.open === 0 && summary.overdue === 0 && summary.completed === 0) {
    return null;
  }

  return (
    <Card className="card-shadow" data-testid="tasks-assigned-widget">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-indigo-600" />
            Current Tasks Assigned
          </CardTitle>
          <CardDescription>
            <span className="flex gap-3 mt-1">
              <span className="text-blue-600">{summary.open} open</span>
              <span className="text-red-600">{summary.overdue} overdue</span>
              <span className="text-green-600">{summary.completed} completed</span>
            </span>
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="text-xs"
            data-testid="tasks-assigned-auto-refresh-toggle"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchAssignedTasks()}
            disabled={refreshing}
            data-testid="tasks-assigned-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="open" className="flex items-center gap-1" data-testid="assigned-open-tab">
              <Clock className="w-3 h-3" />
              Open ({summary.open})
            </TabsTrigger>
            <TabsTrigger value="overdue" className="flex items-center gap-1" data-testid="assigned-overdue-tab">
              <AlertTriangle className="w-3 h-3" />
              Overdue ({summary.overdue})
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-1" data-testid="assigned-completed-tab">
              <CheckCircle className="w-3 h-3" />
              Done ({summary.completed})
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No {activeTab} tasks assigned to others</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {tasks.map((task) => (
                <div 
                  key={task.id} 
                  className={`p-3 rounded-lg border ${task.is_overdue ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950' : 'border-border bg-background'}`}
                  data-testid={`assigned-task-${task.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 
                          className="font-medium text-sm truncate cursor-pointer hover:text-primary hover:underline"
                          onClick={() => openViewDialog(task)}
                          title="Click to view task details"
                        >
                          {task.title}
                        </h4>
                        <Badge className={`${getPriorityColor(task.priority)} text-xs`}>
                          {task.priority}
                        </Badge>
                        {task.is_overdue && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Overdue
                          </Badge>
                        )}
                        {task.status === 'completed' && (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{task.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {task.assigned_to_name && (
                          <span className="flex items-center gap-1 font-medium text-indigo-600">
                            <User className="w-3 h-3" />
                            Assigned to: {task.assigned_to_name}
                          </span>
                        )}
                        {task.due_date && (
                          <span className={`flex items-center gap-1 ${task.is_overdue ? 'text-red-600 font-medium' : ''}`}>
                            <Calendar className="w-3 h-3" />
                            Due: {formatDate(task.due_date)}
                          </span>
                        )}
                        {task.participant_name && (
                          <Link 
                            to={`/participants/${task.participant_id}`} 
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <LinkIcon className="w-3 h-3" />
                            {task.participant_name}
                          </Link>
                        )}
                        {task.link?.url && (
                          <Link to={task.link.url} className="flex items-center gap-1 text-primary hover:underline" data-testid={`task-link-${task.id}`}>
                            <LinkIcon className="w-3 h-3" />
                            {task.link.label}
                          </Link>
                        )}
                        {task.progress_notes?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {task.progress_notes.length} note{task.progress_notes.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {task.attachments?.length > 0 && (
                          <button 
                            className="flex items-center gap-1 text-primary hover:underline cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAttachmentsDialog(task);
                            }}
                            title="View/Download attachments"
                          >
                            <Paperclip className="w-3 h-3" />
                            {task.attachments.length} file{task.attachments.length !== 1 ? 's' : ''}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {task.attachments?.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openAttachmentsDialog(task)}
                          className="h-8 w-8 p-0"
                          title="View/Download attachments"
                          data-testid={`attachments-assigned-task-${task.id}`}
                        >
                          <Paperclip className="w-4 h-4 text-blue-600 hover:text-blue-700" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditTask(task)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-assigned-task-${task.id}`}
                      >
                        <Edit className="w-4 h-4 text-muted-foreground hover:text-primary" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTask(task)}
                        className="h-8 w-8 p-0"
                        data-testid={`delete-assigned-task-${task.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Tabs>
      </CardContent>
      
      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>
              Update the assigned task details.
            </DialogDescription>
          </DialogHeader>
          {editingTask && (
            <DialogBody className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingTask.description || ''}
                  onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={editingTask.priority}
                    onValueChange={(val) => setEditingTask({ ...editingTask, priority: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {taskPriorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editingTask.status}
                    onValueChange={(val) => setEditingTask({ ...editingTask, status: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {taskStatuses.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={editingTask.due_date || ''}
                    onChange={(e) => setEditingTask({ ...editingTask, due_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Select
                    value={editingTask.assigned_to}
                    onValueChange={(val) => setEditingTask({ ...editingTask, assigned_to: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {staffList.map(staff => (
                        <SelectItem key={staff.id} value={staff.id}>
                          {staff.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Attachments Section */}
              <div className="space-y-2">
                <Label>Attachments</Label>
                
                {/* Existing Attachments */}
                {editingTask.attachments?.length > 0 && (
                  <div className="space-y-1 mb-2">
                    <p className="text-xs text-muted-foreground">Existing files:</p>
                    {editingTask.attachments.map((attachment) => (
                      <div key={attachment.id} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[180px]">{attachment.file_name}</span>
                          <span className="text-xs text-muted-foreground">({formatFileSize(attachment.file_size)})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            type="button"
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="h-6 w-6 p-0"
                            title="Download"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button 
                            type="button"
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDeleteExistingAttachment(attachment.id)}
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload New Attachment */}
                <div
                  onDragOver={handleDragOverEdit}
                  onDragLeave={handleDragLeaveEdit}
                  onDrop={handleDropEdit}
                  className={`relative border-2 border-dashed rounded-lg transition-all ${
                    isDraggingEdit 
                      ? 'border-primary bg-primary/10 scale-[1.02]' 
                      : 'border-muted-foreground/25 hover:border-primary hover:bg-muted/50'
                  }`}
                >
                  <label className="flex flex-col items-center justify-center gap-2 p-3 cursor-pointer">
                    <input 
                      type="file" 
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        if (e.target.files?.length > 0) {
                          handleAddEditAttachments(e.target.files);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Upload className={`w-5 h-5 ${isDraggingEdit ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="text-center">
                      <span className={`text-sm ${isDraggingEdit ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                        {isDraggingEdit ? 'Drop files here!' : 'Drag & drop or click to add files'}
                      </span>
                      <p className="text-xs text-muted-foreground">Max 10MB per file</p>
                    </div>
                  </label>
                </div>
                
                {/* Pending New Attachments */}
                {pendingEditAttachments.length > 0 && (
                  <div className="space-y-1 mt-2">
                    <p className="text-xs text-muted-foreground">New files to upload ({pendingEditAttachments.length}):</p>
                    {pendingEditAttachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">({formatFileSize(file.size)})</span>
                        </div>
                        {uploadProgress[`edit_${index}`] !== undefined && uploadProgress[`edit_${index}`] < 100 && (
                          <div className="flex items-center gap-2 mx-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 transition-all duration-300"
                                style={{ width: `${uploadProgress[`edit_${index}`]}%` }}
                              />
                            </div>
                            <span className="text-xs text-green-600">{uploadProgress[`edit_${index}`]}%</span>
                          </div>
                        )}
                        <Button 
                          type="button"
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeEditPendingAttachment(index)}
                          className="h-6 w-6 p-0 flex-shrink-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogBody>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSaveTask(null)} disabled={saving} data-testid="save-assigned-task-changes-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reopen Task Comment Dialog */}
      <Dialog open={showReopenDialog} onOpenChange={(open) => {
        if (!open) handleCancelReopen();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Reopen Completed Task
            </DialogTitle>
            <DialogDescription>
              You are changing a completed task back to <strong className="text-amber-600">{pendingReopenStatus?.replace('_', ' ')}</strong>. 
              Please provide a reason for reopening this task. This comment will be included in the notification email sent to the assignee.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">{editingTask?.title}</p>
              {editingTask?.description && (
                <p className="text-xs text-muted-foreground mt-1">{editingTask.description}</p>
              )}
              {editingTask?.assigned_to_name && (
                <p className="text-xs text-muted-foreground mt-1">Assigned to: {editingTask.assigned_to_name}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="assigned-reopening-comment">Reason for Reopening <span className="text-destructive">*</span></Label>
              <Textarea
                id="assigned-reopening-comment"
                value={reopeningComment}
                onChange={(e) => setReopeningComment(e.target.value)}
                placeholder="Explain why this task needs to be reopened..."
                rows={4}
                data-testid="assigned-reopening-comment-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelReopen}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmReopen} 
              disabled={saving || !reopeningComment.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-assigned-reopen-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reopen Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deletingTask && (
            <div className="py-4">
              <p className="font-medium">{deletingTask.title}</p>
              <p className="text-sm text-muted-foreground">
                Assigned to: {deletingTask.assigned_to_name}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTask} disabled={deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Attachments Dialog */}
      <Dialog open={showAttachmentsDialog} onOpenChange={setShowAttachmentsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Task Attachments
            </DialogTitle>
            <DialogDescription>
              {viewingTask?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {viewingTask?.attachments?.length > 0 ? (
              <div className="space-y-2">
                {viewingTask.attachments.map((attachment) => (
                  <div 
                    key={attachment.id} 
                    className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{attachment.file_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(attachment.file_size)} • {attachment.uploaded_by_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadAttachment(attachment)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteAttachment(viewingTask.id, attachment.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Paperclip className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No attachments yet</p>
              </div>
            )}
            
            {/* Upload new attachment */}
            <div className="mt-4 pt-4 border-t">
              <Label className="text-sm font-medium">Add New Attachment</Label>
              <label className="flex items-center justify-center gap-2 mt-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors">
                <input 
                  type="file" 
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0] && viewingTask) {
                      handleFileUpload(viewingTask.id, e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                  disabled={uploadingAttachment}
                />
                {uploadingAttachment ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Click to upload a file (max 10MB)</span>
                  </>
                )}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttachmentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Task Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Badge className={getPriorityColor(viewingTask?.priority)}>
                {viewingTask?.priority}
              </Badge>
              <Badge variant={viewingTask?.status === 'completed' ? 'secondary' : 'outline'}>
                {viewingTask?.status?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <DialogTitle className="text-xl mt-2">{viewingTask?.title}</DialogTitle>
          </DialogHeader>
          
          {viewingTask && (
            <DialogBody className="space-y-4">
              {viewingTask.description && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">Description</Label>
                  <p className="text-sm whitespace-pre-wrap">{viewingTask.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 py-2">
                {viewingTask.due_date && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Due Date</Label>
                    <p className={`text-sm flex items-center gap-1 ${viewingTask.is_overdue ? 'text-red-600 font-medium' : ''}`}>
                      <Calendar className="w-4 h-4" />
                      {formatDate(viewingTask.due_date)}
                      {viewingTask.is_overdue && <span>(Overdue)</span>}
                    </p>
                  </div>
                )}
                {viewingTask.assigned_to_name && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Assigned To</Label>
                    <p className="text-sm flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {viewingTask.assigned_to_name}
                    </p>
                  </div>
                )}
                {viewingTask.created_by_name && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Created By</Label>
                    <p className="text-sm">{viewingTask.created_by_name}</p>
                  </div>
                )}
                {viewingTask.created_at && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Created</Label>
                    <p className="text-sm">{formatDateTime(viewingTask.created_at)}</p>
                  </div>
                )}
                {viewingTask.status === 'completed' && viewingTask.completed_at && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Completed</Label>
                    <p className="text-sm flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      {formatDateTime(viewingTask.completed_at)}
                    </p>
                  </div>
                )}
              </div>
              
              {viewingTask.participant_name && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">Linked Participant</Label>
                  <Link 
                    to={`/participants/${viewingTask.participant_id}`} 
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <LinkIcon className="w-4 h-4" />
                    {viewingTask.participant_name}
                  </Link>
                </div>
              )}
              
              {viewingTask.link?.url && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">Linked Ticket</Label>
                  <Link to={viewingTask.link.url} className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="task-view-link">
                    <LinkIcon className="w-4 h-4" />
                    {viewingTask.link.label}
                  </Link>
                </div>
              )}

              {viewingTask.attachments?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase">
                    Attachments ({viewingTask.attachments.length})
                  </Label>
                  <div className="space-y-1">
                    {viewingTask.attachments.map((attachment) => (
                      <div 
                        key={attachment.id} 
                        className="flex items-center justify-between p-2 bg-muted rounded"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{attachment.file_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(attachment.file_size)})
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadAttachment(attachment)}
                          data-testid={`download-attachment-${attachment.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {viewingTask.progress_notes?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase">
                    Progress Notes ({viewingTask.progress_notes.length})
                  </Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {viewingTask.progress_notes.map((note, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{note.created_by_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(note.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </DialogBody>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setShowViewDialog(false);
                handleEditTask(viewingTask);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TasksAssignedWidget;
